/**
 * usePragmaticDropTarget.ts - React hook for Pragmatic drop targets
 * 
 * ARCHITECTURE:
 * - Direct wrapper around Pragmatic's dropTargetForElements() function
 * - Returns cleanup function for React useEffect
 * - Provides TypeScript types for better DX
 */

import { useEffect, useRef, RefObject } from 'react';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

export interface UseDropTargetConfig {
  /** Array of draggable types this drop target accepts */
  accepts?: string[];
  
  /** Function to determine if a drop is allowed */
  canDrop?: (args: any) => boolean;
  
  /** Callback when a draggable enters this drop target */
  onDragEnter?: (args: any) => void;
  
  /** Callback when a draggable leaves this drop target */
  onDragLeave?: (args: any) => void;
  
  /** Callback when a draggable is dropped on this target */
  onDrop?: (args: any) => void;
  
  /** Callback when a draggable is over this target (fires frequently) */
  onDrag?: (args: any) => void;
}

/**
 * Hook to make an element a drop target using Pragmatic drag and drop
 * 
 * @example
 * ```tsx
 * const ref = useRef<HTMLDivElement>(null);
 * 
 * usePragmaticDropTarget(ref, {
 *   accepts: ['card', 'field'],
 *   onDragEnter: () => setIsOver(true),
 *   onDragLeave: () => setIsOver(false),
 *   onDrop: ({ source }) => {
 *     console.log('Dropped:', source.data);
 *   }
 * });
 * 
 * return <div ref={ref}>Drop here!</div>;
 * ```
 */
export function usePragmaticDropTarget<T extends HTMLElement>(
  elementRef: RefObject<T>,
  config: UseDropTargetConfig
): void {
  const configRef = useRef(config);
  configRef.current = config;

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const cleanup = dropTargetForElements({
      element,
      canDrop: ({ source }) => {
        // Check if source type is accepted
        if (configRef.current.accepts && configRef.current.accepts.length > 0) {
          const sourceType = source.data.type as string;
          if (!configRef.current.accepts.includes(sourceType)) {
            return false;
          }
        }
        
        // Call custom canDrop if provided
        return configRef.current.canDrop ? configRef.current.canDrop({ source }) : true;
      },
      onDragEnter: configRef.current.onDragEnter,
      onDragLeave: configRef.current.onDragLeave,
      onDrop: configRef.current.onDrop,
      onDrag: configRef.current.onDrag,
    });

    return cleanup;
  }, [elementRef]);
}
